import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DiningOptionBehavior, FulfillmentType } from 'src/apollo/onlineOrdering';
import InputField from 'src/shared/components/common/form_input/InputField';

import Image from 'shared/components/common/Image';
import { ToggleInput } from 'shared/components/common/forms';
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext';

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection';
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection';
import DiningOptionsModal, { formatFulfillmentDateTime } from 'public/components/default_template/online_ordering/dining_options/DiningOptionsModal';
import { Map } from 'public/components/default_template/schedule_map';
import { useCart } from 'public/components/online_ordering/CartContext';
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext';
import { formatDeliveryAddress, formatLocationAddress } from 'public/components/online_ordering/addressUtils';
import { getQuoteTime } from 'public/components/online_ordering/fulfillmentUtils';
import { Channel } from 'public/js/siteUtilities';

import DeliveryPreferencesSection from './DeliveryPreferencesSection';


const FulfillmentSection = ({ complete }: {complete?: boolean}) => {
  const carMake = useWatch({ name: 'curbsidePickupVehicle' });
  const carColor = useWatch({ name: 'curbsidePickupVehicleColor' });
  const { ooRestaurant } = useRestaurant();
  const { cart } = useCart();
  const [curbside, setCurbside] = useState(false);

  if(!cart?.restaurant?.location || !ooRestaurant || !cart?.order) {
    return null;
  }

  const curbsideIndicator = curbside && carMake &&
      <div className="sectionRow">
        <div className="icon">
          <Image alt="Curbside pickup details" src="icons/car-gray.svg" />
        </div>
        {carMake}{carColor && ` (${carColor})`}
      </div>;
  return (
    <CheckoutSection>
      <FulfillmentDetails />
      {complete ? curbsideIndicator : <TakeoutDetails curbside={curbside} setCurbside={setCurbside} />}
    </CheckoutSection>
  );
};

/**
 * TODO: WOO-3130 Move "Save Address" and Delivery Instructions to this panel from CustomerInfoSection.
 * https://toasttab.atlassian.net/browse/WOO-3130
 * const { selectedVariant: deliveryAddressTest } = useExperiment('woo-delivery-addr-revision');
 */
/** Renders the specifics of how the order is being fulfilled, e.g. dining behavior, time, location */
export const FulfillmentDetails = () => {
  const { canUpdateFulfillment, setFulfillmentModalOpen } = useFulfillment();
  const { cart, channel } = useCart();
  const { ooRestaurant } = useRestaurant();

  const minimumTime = useMemo(() => cart && ooRestaurant ? getQuoteTime(cart.diningOptionBehavior, ooRestaurant, cart) : null, [cart, ooRestaurant]);
  const diningOptionButtonOnClick = useCallback( async () => {
    if(canUpdateFulfillment) {
      await setFulfillmentModalOpen(true);
    }
  }, [canUpdateFulfillment, setFulfillmentModalOpen]);

  if(!cart?.restaurant?.location || !ooRestaurant || !cart?.order) {
    return null;
  }
  return (
    <div tabIndex={0} role="group">
      <h2 className="subHeader rxName">{ooRestaurant.whiteLabelName || cart.restaurant.name}</h2>
      <div>{formatLocationAddress(cart.restaurant.location)}</div>
      {channel === Channel.OO &&
        <>
          <hr tabIndex={-1} aria-hidden />
          {canUpdateFulfillment &&
          <button type="button" aria-label="Edit order fulfillment options" className="diningOptionsLink" onClick={diningOptionButtonOnClick}>Edit</button>}
          <div className="sectionRow">
            {cart.diningOptionBehavior === DiningOptionBehavior.Delivery ?
              <><div className="icon"><Image alt="Order type" src="icons/delivery.svg" /></div><div>Delivery to {formatDeliveryAddress(cart.order.deliveryInfo)}</div></> :
              <><div className="icon"><Image alt="Order type" src="icons/pickup.svg" /></div><div>Pickup</div></>}
          </div>
          <div className="sectionRow">
            <div className="icon"><Image alt="Order time" src="icons/calendar.svg" /></div>
            {cart.fulfillmentType === FulfillmentType.Asap && minimumTime ?
              `ASAP (Est. ${minimumTime}-${minimumTime + 5} minutes)` :
              formatFulfillmentDateTime(cart.fulfillmentDateTime)}
          </div>
          {cart.diningOptionBehavior === DiningOptionBehavior.TakeOut &&
          <div className="sectionRow">
            <div className="icon"><Image alt="Location" src="icons/location.svg" /></div>
              You can pick up your order at {cart.restaurant.location.address1}{cart.restaurant.location.address2 ? `, ${cart.restaurant.location.address2}` : ''}
          </div>}
          <div className="fulfillmentDeliveryPreferences">
            <DeliveryPreferencesSection />
          </div>
          <DiningOptionsModal />
        </>}
    </div>
  );
};

/** Renders map and curbside pickup options details for takeout orders */
export const TakeoutDetails = ({ curbside, setCurbside }: {curbside: boolean, setCurbside: (curbside: boolean) => void}) => {
  const { cart, channel } = useCart();
  const { ooRestaurant } = useRestaurant();
  const { register } = useFormContext();
  const showCurbside = useMemo(() =>
    (ooRestaurant?.curbsidePickupConfig?.enabled || ooRestaurant?.curbsidePickupConfig?.enabledV2)
  && cart?.diningOptionBehavior === DiningOptionBehavior.TakeOut && channel !== Channel.ECOMM,
  [ooRestaurant?.curbsidePickupConfig, cart?.diningOptionBehavior, channel]);
  if(cart?.diningOptionBehavior !== DiningOptionBehavior.TakeOut || !cart?.restaurant?.location || !ooRestaurant || !cart?.order) {
    return null;
  }
  return (
    <>
      <Map
        name={cart.restaurant.name}
        address1={cart.restaurant.location.address1}
        address2={cart.restaurant.location.address2}
        city={cart.restaurant.location.city}
        state={cart.restaurant.location.state}
        zipcode={cart.restaurant.location.zip}
        alwaysShowMap={channel !== Channel.ECOMM} />
      {showCurbside &&
        <div data-testid="curbside-pickup-section">
          <ToggleInput
            type="checkbox"
            id="curbsidePickup"
            dataTestId="curbsidePickupCheckbox"
            checked={curbside}
            onKeyDown={event => {
              if(event.key === 'Enter') {
                setCurbside(!curbside);
              }
            }}
            ariaLabel="curbside-label"
            {...register('curbsidePickup', { onChange: () => setCurbside(!curbside) })}>
            <div className="curbsideText" id="curbside-label">
              <span className="bold">Curbside pickup</span>
              <br className="hidden-sm-up" />
              <span className="gray">(bring my order to my car)</span>
            </div>
          </ToggleInput>
          <AnimatedSection expanded={Boolean(showCurbside && curbside)} testid="curbside-animated-section">
            <div className="curbsidePickupVehicle">
              {/* max length values from
              https://github.com/toasttab/toast-orders/blob/main/toast-orders-application/src/main/java/com/toasttab/service/orders/resources/orika/mappers/CurbsidePickupMapper.kt */}
              <InputField maxLength={100} id="curbsidePickupVehicle" label="Vehicle Make/Model" required={curbside} preventFormSubmit />
              <InputField maxLength={20} id="curbsidePickupVehicleColor" label="Vehicle Color" preventFormSubmit />
            </div>
          </AnimatedSection>
        </div>}
    </>
  );
};

export default FulfillmentSection;
